import * as React from 'react';
import { useField } from 'formik';
import { BootstrapSelect } from './SelectField';
import { useFieldRequired } from './FormSchemaContext';
import {
  CircularProgress,
  ClickAwayListener,
  FormControl,
  FormControlProps,
  FormHelperText,
  FormHelperTextProps,
  IconButton,
  Input,
  InputAdornment,
  InputLabel,
  ListItem,
  MenuItem,
  SelectChangeEvent,
  SelectProps,
  TextFieldProps,
  Theme,
  Typography,
  useTheme,
} from '@mui/material';
import SearchSharpIcon from '@mui/icons-material/SearchSharp';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import theme from '../../themes';

export interface IKeyValuePair {
  key: any;
  value: string;
}

interface IBaseProps {
  label?: string;
  searchFieldPlaceholder?: string;
  removeSelectionText?: string;
  helperText?: string;
  formControlProps?: FormControlProps;
  formHelperTextProps?: FormHelperTextProps;
  maxVisibleOptions?: number;
  showAll?: boolean;
  noRemoveSelectionOption?: boolean;
}

interface IDefaultKeyValuePair extends IBaseProps {
  options: IKeyValuePair[];
}

interface ICustomKeyValuePair extends IBaseProps {
  keyPropFn: (option: IKeyValuePair | any) => any;
  valuePropFn: (option: IKeyValuePair | any) => string | number;
  options: any[];
}

export type SearchableSelectProps = (IDefaultKeyValuePair | ICustomKeyValuePair) & SelectProps;

interface IClickAwayListenerWrapperProps {
  searchFieldPlaceholder: string | undefined;
  setQuery: React.Dispatch<React.SetStateAction<string>>;
}

export function SearchFieldWrapper({
  searchFieldPlaceholder,
  setQuery,
}: IClickAwayListenerWrapperProps & {
  searchFieldPlaceholder: string;
  setQuery: React.Dispatch<React.SetStateAction<string>>;
}): JSX.Element {
  const inputRef = React.useRef<HTMLInputElement>(null);
  React.useEffect(() => {
    const timer = setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
    return () => clearTimeout(timer);
  }, []);

  return (
    <ClickAwayListener onClickAway={() => null}>
      <ListItem>
        <Input
          id="input-with-icon-adornment"
          inputRef={inputRef}
          autoFocus
          placeholder={searchFieldPlaceholder || 'Search...'}
          inputProps={{
            sx: {
              '&::placeholder': { color: 'text.secondary', opacity: 1 },
            },
          }}
          autoComplete="off"
          fullWidth
          onChange={(e) => {
            setQuery(e.target.value);
          }}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
              const menuList = inputRef.current?.closest('ul');
              const menuItems = menuList?.querySelectorAll<HTMLElement>(
                'li.MuiMenuItem-root:not(.Mui-disabled)'
              );
              if (menuItems && menuItems.length > 0) {
                e.preventDefault();
                e.stopPropagation();
                const target =
                  e.key === 'ArrowDown'
                    ? menuItems[0]
                    : menuItems[menuItems.length - 1];
                target.focus();
              }
              return;
            }

            if (e.key === 'Escape') return;

            e.stopPropagation();
          }}
          startAdornment={
            <InputAdornment position="start">
              <IconButton
                tabIndex={-1}
                disableRipple
                sx={{
                  p: 0,
                  '&:hover': { backgroundColor: 'transparent' },
                }}>
                <SearchSharpIcon
                  sx={{
                    color: 'primary.main',
                    '&:hover': {
                      outline: `2px solid ${theme.palette.primary[500]}`,
                      outlineOffset: '2px',
                    },
                  }}
                />
              </IconButton>
            </InputAdornment>
          }
        />
      </ListItem>
    </ClickAwayListener >
  );
}

export function renderFilteredOptions(
  items: { key: string | number; value: string | number }[],
  query: string
): any {
  const filteredOptions =
    items &&
    items.filter &&
    items.filter((item: IKeyValuePair | any) => {
      return (
        !item.key ||
        (item.key && item.key.toString().toLowerCase().indexOf(query.toLowerCase()) !== -1)
      );
    });

  return filteredOptions?.length > 0 ? (
    filteredOptions.map(
      (
        option:
          | {
            key: string | number;
            value: string | number;
          }
          | any,
        index
      ) => {
        return (
          <MenuItem
            key={`${option.key}-${index}`}
            value={option.value}
            sx={{
              fontSize: 14,
              fontWeight: 500,
              color: 'text.primary',
              '&.Mui-selected,&.Mui-selected:hover': { backgroundColor: 'unset' },
            }}>
            <Typography
              sx={{ fontSize: 14, whiteSpace: { xs: 'pre-wrap', sm: 'nowrap' } }}
              component="span">
              {option.key}
            </Typography>
          </MenuItem>
        );
      }
    )
  ) : (
    <Typography
      sx={{
        paddingTop: '6px',
        paddingBottom: '6px',
        boxSizing: 'border-box',
        whiteSpace: 'nowrap',
        paddingLeft: '16px',
        paddingRight: '16px',
        fontSize: '14px',
        fontWeight: 500,
      }}>
      No Results found
    </Typography>
  );
}

export function SearchableSelect({
  name,
  label,
  items,
  onChange,
  disabled = false,
  searchFieldPlaceholder = '',
  applyRenderValue = false,
  loading = false,
  open: openProp,
  onOpen: onOpenProp,
  onClose: onCloseProp,
  SelectDisplayProps: SelectDisplayPropsProp,
  ...props
}: SelectProps & {
  name: string;
  label?: string;
  items: { key: string | number; value: string | number }[];
  onChange?: (event: SelectChangeEvent<unknown>) => void;
  disabled?: boolean;
  searchFieldPlaceholder?: string;
  applyRenderValue?: boolean;
  loading?: boolean;
}): JSX.Element {
  const id = `${name}-input`;
  const labelId = `${id}-label`;

  const theme = useTheme();
  const [query, setQuery] = React.useState('');
  const [field, meta] = useField(name);
  onChange = onChange || field.onChange;
  const errorText = meta.error && meta.touched ? meta.error : '';
  const errorId = `${name}-error`;
  const isRequired = useFieldRequired(name);
  const [isOpen, setIsOpen] = React.useState(false);
  const isControlledOpen = openProp !== undefined;
  const expanded = isControlledOpen ? Boolean(openProp) : isOpen;

  return (
    <FormControl fullWidth sx={{ my: { xs: 1, sm: 1, md: 2 } }}>
      {label && (
        <InputLabel
          // shrink
          htmlFor={id}
          sx={(theme: Theme) => ({
            // transform: 'unset',
            // fontSize: 14,
            // fontWeight: 500,
            // color: 'rgba(0,0,0,0.7)'
            color: disabled ? `${theme.palette.text.secondary}B3` : theme.palette.text.secondary,

            ...theme.typography.body_500,
            '&.Mui-focused': {
              color: theme.palette.primary[700],
            },
          })}>
          {label}
        </InputLabel>
      )}
      <BootstrapSelect
        labelId={labelId}
        label={label}
        id={id}
        sx={{
          // mt: '32px',
          ...props.sx,
          '& .Mui-disabled': {
            cursor: !disabled ? 'pointer' : 'not-allowed !important',
            pointerEvents: 'all',
          },
          '& .MuiSelect-icon': {
            width: '1em !important',
            height: '1em !important',
          },
        }}
        MenuProps={{
          sx: {
            '.MuiPaper-root ': {
              maxWidth: { xs: '100%', sm: '31%', md: '26%' },
              maxHeight: '20%',
              overflowX: 'auto',
              '& .MuiMenuItem-root.Mui-focusVisible': {
                outline: `2px solid ${theme.palette.primary[500]} !important`,
                outlineOffset: '-2px',
              },
            },
          },
          disableAutoFocusItem: true,
          MenuListProps: {
            disableListWrap: true,
          },
        }}
        placeholder="ssss"
        {...field}
        onChange={onChange}
        disabled={disabled}
        renderValue={(Selectedvalue) => {
          const selectedKey = items.find((_item) => _item.value == Selectedvalue);
          return (
            <Typography
              variant='body_500'
              sx={{
                // fontSize: 14,
                whiteSpace: { xs: 'pre-wrap', sm: 'nowrap' }
              }}
              component="span">
              {applyRenderValue
                ? (selectedKey?.key as unknown as string)?.split('- ')[1]
                : (selectedKey?.key as unknown as string)}
            </Typography>
          );
        }}
        IconComponent={loading ? CircularProgress : ExpandMoreIcon}
        {...props}
        open={openProp}
        onOpen={(event) => {
          if (!isControlledOpen) setIsOpen(true);
          onOpenProp?.(event);
        }}
        onClose={(event) => {
          setQuery('');
          if (!isControlledOpen) setIsOpen(false);
          onCloseProp?.(event);
        }}
        SelectDisplayProps={{
          'aria-expanded': expanded,
          'aria-labelledby': label ? labelId : undefined,
          'aria-label': label ?? '',
          'aria-describedby': errorText ? errorId : undefined,
          'aria-invalid': errorText ? true : undefined,
          'aria-required': isRequired || undefined,
          ...(SelectDisplayPropsProp || {}),
        }}>
        <SearchFieldWrapper searchFieldPlaceholder={searchFieldPlaceholder} setQuery={setQuery} />
        {renderFilteredOptions(items, query)}
      </BootstrapSelect>
      {
        errorText && (
          <FormHelperText
            id={errorId}
            role="alert"
            error
            sx={{ marginLeft: 'unset', color: `${theme.palette.error[900]}`, fontWeight: 500 }}
          >
            {errorText}
          </FormHelperText>
        )
      }
    </FormControl >
  );
}
